/* eslint-disable */
import { Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { MSRemoteLoggerCategory, MSRemoteLoggerSeverity } from 'src/app/adapters/remote-logger/utils/remote-logger.utils';
import { RemoteLoggerUseCase } from 'src/app/use-case/remote-logger/remote-logger.use-case';
import { CONSTANTS } from '../config.service';
import { DateTimeService } from '../date-time/date-time.service';
import { LoggerService } from '../logger.service';
import { PersonsService } from '../persons.service';
import { PrefsService } from '../prefs.service';
import { UtilsService } from '../utils.service';
import { MatcherService } from './matcher.service';

@Injectable({
  providedIn: 'root'
})
export class LoadService {
  surveyPages: any = {};

  constructor(
    private alertCtrl: AlertController,
    private logger: LoggerService,
    private prefs: PrefsService,
    private utils: UtilsService,
    private dateTime: DateTimeService,
    private persons: PersonsService,
    private matcher: MatcherService,
    private remoteLogger: RemoteLoggerUseCase
  ) {}

  getSurveyPages(surveyType) {
    return this.surveyPages[surveyType];
  }

  async presentAlert(msg) {
    const alert = await this.alertCtrl.create({
      header: 'Programmer Error',
      message: msg,
      buttons: [
        {
          text: 'Thanks for the heads up',
          handler: () => {
            throw new Error(msg);
          }
        }
      ]
    });
    await alert.present();
  }

  initSurveys(surveyDefs) {
    let loopPages = [];
    let loopEndPageName;
    let totalLaps;
    let surveyPagesInPrefs = {};
    let hiddenFieldsInPrefs = {};

    return Promise.all([this.prefs.getPromiseOrDefault('surveyPages'), this.prefs.getPromiseOrDefault('hiddenFields')]).then((res) => {
      if (res[0]) {
        surveyPagesInPrefs = res[0];
      }
      if (res[1]) {
        hiddenFieldsInPrefs = res[1];
      }

      surveyDefs.forEach((surveyDef) => {
        let pages = [];
        const expandLoopPages = () => {
          const out = [];
          let currLap;
          let clonedPage;
          const throwIfAlreadySuffixed = (name) => {
            if (/L[0-9]+$/.test(name)) {
              throw new Error(name + 'in a loop is just awkward');
            }
          };
          const tweakPage = (page) => {
            const suffix = 'L' + currLap;
            throwIfAlreadySuffixed(page.name);
            clonedPage = JSON.parse(JSON.stringify(page));
            clonedPage.lap = currLap;
            clonedPage.maxLapIndex = totalLaps - 1;
            clonedPage.name = clonedPage.name + suffix;
            clonedPage.questions.forEach((q) => {
              throwIfAlreadySuffixed(q.name);
              q.name = q.name + suffix;
              //checkbox questions need to have their loop added to the dbfields of each item
              if (q.type == 'checkbox') {
                q.items.forEach((item) => {
                  item.dbField = '_CB_' + q.name + '_' + item.id;
                });
              }
            });
            out.push(clonedPage);
          };

          for (currLap = 0; currLap < totalLaps; currLap++) {
            // HACK_LAP
            loopPages.forEach(tweakPage);
          }
          return out;
        };

        if (CONSTANTS.MAX_RECIPE_VERSION < surveyDef.recipeVersion || surveyDef.recipeVersion < CONSTANTS.MIN_RECIPE_VERSION) {
          const msg =
            'Unsupported survey version (' +
            surveyDef.recipeVersion +
            ' sb between ' +
            CONSTANTS.MIN_RECIPE_VERSION +
            ' and ' +
            CONSTANTS.MAX_RECIPE_VERSION +
            ')';
          this.presentAlert(msg);
        }

        surveyDef.pages.forEach((page) => {
          if (page.loop) {
            // 1st loop page
            if (loopEndPageName) {
              throw new Error('Loop already running (through ' + loopEndPageName + ') and ' + page.name + ' tried starting a new one');
            }
            if (!page.loop.throughPage || !page.loop.laps) {
              throw new Error('Unexpected loop params ' + page.loop);
            }
            loopEndPageName = page.loop.throughPage;
            totalLaps = page.loop.laps;
          }
          if (loopEndPageName) {
            // within loop
            if ('end' === page.name) {
              throw new Error('End page within loop');
            }
            loopPages.push(page);
            if (loopEndPageName === page.name) {
              // last loop page
              loopEndPageName = null;
              pages = pages.concat(expandLoopPages());
              loopPages = [];
            }
          } else {
            pages.push(page);
          }

          if (page.questions) {
            page.questions.forEach((question) => {
              if (question.type === 'checkbox') {
                if (question.items && question.items.length) {
                  question.items.forEach((answer) => {
                    // NOTE: In angular 1, we stored surveyPages in rootscope and 'dbField' was added to checkboxes in the schema provider.
                    // In angular 2+, we're storing surveyPages in the load provider, so we need to add dbField here
                    // HACK_CB: _CB_ is so survey senders (in js and Java) know to transform the data into the format expected by the server
                    //    using this b/c Java back-end only has access to survey data and not survey objects
                    //    putting this on the answer in the rootScope so survey reading/writing doesn't need to duplicate this name

                    answer.dbField = '_CB_' + question.name + '_' + answer.id;
                  });
                }
              }
            });
          }
        });

        surveyPagesInPrefs[surveyDef.surveyType] = pages;
        this.surveyPages[surveyDef.surveyType] = pages;

        hiddenFieldsInPrefs[surveyDef.surveyType] = surveyDef.hiddenFields || [];
      });

      return Promise.all([
        this.prefs.getStorePromise('surveyPages', surveyPagesInPrefs),
        this.prefs.getStorePromise('hiddenFields', hiddenFieldsInPrefs)
      ]);
    });
  }

  getPageByName = (surveyType, pageName, surveyData, isClearingSkippedVars, isGettingLabels) => {
    this.remoteLogger.addBreadcrumbs('getPageByName', MSRemoteLoggerCategory.SURVEY_LOAD_SERVICE, MSRemoteLoggerSeverity.INFO, {
      surveyType,
      pageName,
      isClearingSkippedVars,
      isGettingLabels
    });

    const pages = this.getSurveyPages(surveyType);
    const page = pages.filter((p) => p.name === pageName)[0];
    // // return a copy b/c we modify it each time...
    const pageCopy = JSON.parse(JSON.stringify(page));
    let surveySubject = {};
    const getValues = (questionNames, labelType) => {
      // works for multi question types, e.g. textboxes, selects
      let i = 0;
      let value;
      const values = [];
      let qNames = [];
      let refPage;

      if (!questionNames) {
        return null;
      }

      //allows recipe to specify a single question name as a string, or an array of strings
      if (!Array.isArray(questionNames)) {
        qNames.push(questionNames);
      } else {
        qNames = questionNames;
      }

      //if keying off radio button question rather than textboxes creates Array of correct length, filled with undefined
      if (labelType === 'radio') {
        return new Array(surveyData[qNames[0]]).fill(undefined);
      }

      //if keying off answer option labels
      if (labelType === 'item') {
        refPage = pages.filter((p) => p.name === qNames[0])[0];
        refPage.questions[0].items.forEach((item) => {
          values.push(item.text);
        });
      }

      if (labelType === 'text') {
        qNames.forEach((q) => {
          //can pull from single textbox or multi textboxes
          if (surveyData[q] !== undefined) {
            values.push(surveyData[q]);
          } else {
            for (i = 0; true; i++) {
              value = surveyData[q + '_' + i];
              if (value !== undefined && value !== null && value !== '') {
                values.push(value);
                continue;
              }
              break;
            }
          }
        });
      }

      // special case to create row labels from vehicle entries
      if (labelType === 'vehicles') {
        // max vehicles is 8 (from 0-7)
        // TODOJS - make max vehs dynamic and handle for trip/daily surveys too
        for (i = 0; i < 8; i++) {
          const year = surveyData['hhVehicles_year_' + i];
          const make = surveyData['hhVehicles_make_' + i];
          const model = surveyData['hhVehicles_model_' + i];
          const other = surveyData['hhVehicles_other_' + i];

          if (year == null) {
            break;
          }

          if (year == CONSTANTS.YEAR_MAKE_MODEL_OTHER) {
            values.push(other);
          } else if (make == CONSTANTS.YEAR_MAKE_MODEL_OTHER) {
            values.push(year + ' ' + other);
          } else if (model == CONSTANTS.YEAR_MAKE_MODEL_OTHER) {
            values.push(year + ' ' + make + ' ' + other);
          } else {
            values.push(year + ' ' + make + ' ' + model);
          }
        }
      }

      return values;
    };
    const loadItems = (question) => {
      let labels;
      let values;
      let initialEntries;

      // toggleIndex is for loop questions (selects/textboxes) - like which people in the hh have smartphones?
      const getItemToggler = (toggler, i) => {
        this.remoteLogger.addBreadcrumbs('getItemToggler', MSRemoteLoggerCategory.SURVEY_LOAD_SERVICE, MSRemoteLoggerSeverity.INFO, {
          question,
          toggler,
        });
        const clone = JSON.parse(JSON.stringify(toggler));
        if (Array.isArray(toggler)) {
          return { all: clone, toggleIndex: i };
        } else {
          clone.toggleIndex = i;
          return clone;
        }
      };
      const addItemTogglers = (item, i) => {
        if (question.params.hideItems) {
          item.hide = getItemToggler(question.params.hideItems, i);
        }
        if (question.params.showItems) {
          item.show = getItemToggler(question.params.showItems, i);
        }
      };
      const getTextboxesItem = (ignoredValue, i) => {
        const item = { id: i };
        addItemTogglers(item, i);
        return item;
      };
      // creates a select question per label (with that label, like for each person name)
      // toggles the question
      // sets matchers on options
      const setSelectsQuestions = (labels, items) => {
        const promises = [];
        question.questions = [];

        labels.forEach((label, i) => {
          const toggler = { hide: undefined, show: undefined };
          if (question.params.hideItems) {
            toggler.hide = getItemToggler(question.params.hideItems, i);
          }
          if (question.params.showItems) {
            toggler.show = getItemToggler(question.params.showItems, i);
          }
          promises.push(
            this.matcher.show(toggler, surveyData).then((shown) => {
              if (shown) {
                this.remoteLogger.addBreadcrumbs('setSelectsQuestions', MSRemoteLoggerCategory.SURVEY_LOAD_SERVICE, MSRemoteLoggerSeverity.INFO, {
                  label,
                  items,
                });
                const theseItems = JSON.parse(JSON.stringify(items));

                theseItems.forEach((item) => {
                  if (item.hide) {
                    item.hide = getItemToggler(item.hide, i);
                  }
                  if (item.show) {
                    item.show = getItemToggler(item.show, i);
                  }
                });

                question.questions.push({
                  text: label,
                  id: i,
                  items: theseItems
                });
              }
            })
          );
        });

        return Promise.all(promises);
      };

      /*
       * for textboxes and selects q types, pull the row labels from either:
       * 1) radio - radio button answer (which provides a count of rows to create but with no labels)
       * 2) text - a textboxes question (which provides values to use as row labels)
       * 3) item - a question's item labels (i.e., answer option text)
       */

      if (question.params && question.params.entryPerAForQ) {
        labels = getValues(question.params.entryPerAForQ, question.params.labelType);

        if (question.type === 'selects' || question.type === 'vehicles') {
          return setSelectsQuestions(labels, question.items);
        }

        question.items = [];
        labels.forEach((label, i) => {
          let item;
          switch (question.type) {
            case 'checkbox':
              item = {
                id: i,
                text: label,
                dbField: '_CB_' + question.name + '_' + i
              };
              break;
            case 'radio':
            case 'textboxes':
            case 'geocoders':
              item = {
                id: i,
                text: label
              };
              break;
          }
          if (item) {
            addItemTogglers(item, i);
            question.items.push(item);
          }
        });
      }

      //for a textboxes q type, populate the values or add 1 box to initialize entries
      if (question.type === 'textboxes' && (!labels || labels.length === 0)) {
        values = getValues(question.name, null);
        if (values.length > 0) {
          question.items = values.map(getTextboxesItem);
        } else {
          initialEntries = question.params.minEntries || 1; // 0 returns 1 here
          question.items = new Array(initialEntries).fill(undefined).map(getTextboxesItem);
        }
      }
    };
    // turns some items (answers) off
    const toggleItems = (items) => {
      // recursion to maintain context w/ promises (possibly async)
      // backwards so indices are correct after deletions
      const toggle = (i) =>
        //logger.log('toggling ' + i + ' of ' + items.length);
        this.matcher.show(items[i], surveyData).then((show) => {
          //logger.log((show ? 'Showing ' : 'Hiding ') + items[i].text);
          if (!show) {
            items.splice(i, 1);
          }
          if (--i >= 0) {
            return toggle(i);
          }
        });
      if (items && items.length > 0) {
        return toggle(items.length - 1);
      } else {
        return Promise.resolve(); // need to toggle rows/cols?
      }
    };
    const setSurveySubject = () => {
      // TODO getMemberById should return a member directly instead of an [] containing that member
      if (surveyData.subjectPersonId) {
        return this.persons.getPersonById(surveyData.subjectPersonId).then((person) => {
          surveySubject = person;
        });
      } else {
        return Promise.resolve(null);
      }
    };
    const replaceText = () => {
      // not sure what needs to be handled yet - just handling what we know about
      const promises = [];
      const pickText = (element) => {
        // resolves textChoice or returns text attr
        const text = element.text;
        let i = 0;
        const getOrReject = (choice) => {
          const test = choice.if || choice.elif;
          const conditionals = (choice.if ? 1 : 0) + (choice.elif ? 1 : 0) + (choice.hasOwnProperty('else') ? 1 : 0);

          if (conditionals !== 1) {
            throw 'Exactly 1 of [if, elif, else] expected in ' + JSON.stringify(choice);
          }

          if (choice.hasOwnProperty('else')) {
            return Promise.resolve(choice.text);
          } else {
            return this.matcher
              .conditionsMatch(test, surveyData)
              .then((passed) => (passed ? Promise.resolve(choice.text) : Promise.reject('testFailure')));
          }
        };
        const chooseText = () =>
          getOrReject(element.textChoice[i])['catch']((testFailReason) => {
            if (++i < element.textChoice.length) {
              return chooseText();
            } else {
              throw 'Text choice failed (include "else"?) ' + JSON.stringify(element.textChoice);
            }
          });

        if (!element.textChoice) {
          return Promise.resolve(text);
        }

        return chooseText();
      };
      const replaceValues = (text) => {
        const re = /<<([^>]+)>>/g;
        const out = [];
        let start = 0;
        let match;
        const getValue = (expression) => {
          // all usages so far: person.name, surveyDate|ymdToLongDate, surveyDate|ymdToShortDate (social network), segment.label (rPlace)
          const pipeSplit = expression.split('|');
          const dotSplit = pipeSplit[0].split('.');
          const filter = pipeSplit.length > 1 ? pipeSplit[1] : null;
          const scopeName = dotSplit.length > 1 ? dotSplit[0] : null;
          let scope;
          const varName = dotSplit.length > 1 ? dotSplit[1] : dotSplit[0];
          let value = '';

          // TODO - figure this out
          if (scopeName) {
            scope = { survey: surveyData, person: surveySubject }[scopeName.trim()];
          } else {
            // No root scope any more, but doesn't seem like it was being used
            //scope = $rootScope;
          }

          if (scope) {
            value = scope[varName.trim()];
            // filters have been used exclusively for date manipulation. updating to use moment
            if (filter) {
              value = this.dateTime[filter.trim()](value);
              //value = $filter(filter.trim())(value);
            }
          }

          return value;
        };

        while ((match = re.exec(text))) {
          out.push(text.substring(start, match.index)); // up to match
          out.push(getValue(match[1].trim())); // replacement
          this.logger.log(start, MSRemoteLoggerCategory.SURVEY_LOAD_SERVICE, match[0]);
          start = match.index + match[0].length; // end of match
          this.logger.log(start, MSRemoteLoggerCategory.SURVEY_LOAD_SERVICE, match);
        }
        //logger.log(out);
        out.push(text.substr(start)); // after last match
        //logger.log(text, out, start);
        return out.join('');
      };
      const replaceTextInArray = (array) => {
        array.forEach((a) => {
          promises.push(
            pickText(a).then((text) => {
              if (!text) {
                // we can have an empty [] of items as placeholders
                return;
              }
              a.text = replaceValues(text);
              return Promise.resolve();
            })
          );
        });
      };

      replaceTextInArray(pageCopy.questions);
      pageCopy.questions.forEach((question) => {
        if (question.items) {
          // not doing rows/cols - replace those with items
          replaceTextInArray(question.items);
        }
      });
      return Promise.all(promises);
    };
    const toggleSubtext = (surveyData) => {
      const promises = [];
      pageCopy.questions.forEach((question) => {
        if (question.subtext && question.showSubtext) {
          promises.push(
            this.matcher.conditionsMatch(question.showSubtext, surveyData).then((match) => {
              if (!match) {
                question.subtext = null;
              }
            })
          );
        }
      });

      return Promise.all(promises);
    };
    const filterQuestions = (surveyData) => {
      this.logger.log('nextPreRandomized: load', MSRemoteLoggerCategory.SURVEY_LOAD_SERVICE);
      return this.utils.filterWithPromise(pageCopy.questions, (q) => this.matcher.show(q, surveyData));
    };

    if (!pageCopy) {
      throw pageName + ' N/A in load.getPageByName';
    }

    // TODO do this once instead of in load and branch?
    // surveyData['lap'] = pageCopy.lap;
    // surveyData.maxLapIndex = pageCopy.maxLapIndex;
    return filterQuestions(surveyData)
      .then((filteredQs) => {
        pageCopy.questions = filteredQs;
      })
      .then(() => {
        toggleSubtext(surveyData);
      })
      .then(() => {
        const promises = [];

        pageCopy.questions.forEach((question) => {
          promises.push(loadItems(question));
        });

        return Promise.all(promises);
      })
      .then(() => {
        const promises = [];

        if (isClearingSkippedVars || isGettingLabels) {
          return Promise.resolve(null);
        }

        pageCopy.questions.forEach((question) => {
          //if selects question type, run toggleItems for each row
          if (question.questions) {
            question.questions.forEach((q) => {
              promises.push(toggleItems(q.items));
            });
          } else {
            promises.push(toggleItems(question.items));
          }
        });

        return Promise.all(promises);
      })
      .then((res) => {
        if (isClearingSkippedVars) {
          return Promise.resolve();
        }
        return setSurveySubject();
      })
      .then(() => {
        if (isClearingSkippedVars) {
          return Promise.resolve(null);
        }
        return replaceText();
      })
      .then((res) => pageCopy);
  };

  initialize() {
    this.logger.log('Initializing survey loader', MSRemoteLoggerCategory.SURVEY_LOAD_SERVICE);
    return this.prefs.getPromise(CONSTANTS.PREF_KEY_SURVEY_RECIPES).then((surveyDefs) => this.initSurveys.call(this, surveyDefs));
  }

  initSignupSurvey() {
    return this.prefs.getPromise(CONSTANTS.PREF_KEY_SIGNUP_SURVEY_RECIPE).then((surveyDef) => this.initSurveys.call(this, [surveyDef]));
  }
}
